import { EXPORT_FPS } from '@/lib/timeline/exportFrameTiming';
import { buildSceneLayout } from '@/lib/transitions/buildSceneLayout';
import { sourceTimeToOutputTime } from '@/lib/transitions/mapTimes';
import { sanitizeTransitions } from '@/lib/transitions/types';
import { sanitizeCoverImage } from '@/lib/cover/coverImage';
import { resolveCropCenterXAtTime } from '@/lib/cover/resolveCropCenterXAtTime';
import { frameCaptureSourceTime } from './resolveFrameCaptureTime';
import { sanitizeCropCenterKeyframes } from './frameCaptureTargets';
import type { QuoteFrameSource, QuoteSlideBackground } from './types';

/** The two videos a frame background can come from; null = unavailable.
 *  `original` = the project's/library's full source video (source axis);
 *  `edited` = the clip's rendered export (its own export axis). */
export interface FrameCaptureSources {
  original: string | null;
  edited: string | null;
}

type Interval = { start: number; end: number };

/**
 * The "edited" video: the rendered export (`finished_video`). Unavailable
 * without a render, or once any of the clip's media expired (same rule as
 * the clip cards' `isExpired`).
 */
export function resolveEditedFrameSource(clip: {
  finished_video_url?: string | null;
  clipping_status?: string | null;
  preparing_status?: string | null;
  rendering_status?: string | null;
}): string | null {
  const expired = clip.clipping_status === 'expired'
    || clip.preparing_status === 'expired'
    || clip.rendering_status === 'expired';
  if (expired) return null;
  return clip.finished_video_url || null;
}

/**
 * A SOURCE-video time → the same moment on the rendered export's timeline.
 * Uses the editor/export's own placement (`buildSceneLayout` +
 * `sourceTimeToOutputTime` at `EXPORT_FPS`), so overlap transitions that
 * shorten the output are honored. The worker prepends a valid cover image as
 * exactly one extra frame, which shifts everything by 1/EXPORT_FPS. Null when
 * the axis is unknown (no kept intervals).
 */
export function resolveEditedFrameTime(args: {
  sourceTime: number;
  intervals: Interval[];
  transitions: unknown;
  coverImage: unknown;
}): number | null {
  const {
    sourceTime, intervals, transitions, coverImage,
  } = args;
  if (intervals.length === 0) return null;
  const scenes = intervals.map((iv) => ({ startTime: iv.start, endTime: iv.end }));
  const layout = buildSceneLayout({
    scenes,
    transitions: sanitizeTransitions(transitions, scenes.length),
    fps: EXPORT_FPS,
  });
  const output = sourceTimeToOutputTime(scenes, layout.blocks, sourceTime, EXPORT_FPS);
  const coverOffset = sanitizeCoverImage(coverImage) ? 1 / EXPORT_FPS : 0;
  return Math.max(0, output + coverOffset);
}

/** The automatic capture time of a quote on the chosen source's own axis:
 *  the source time itself for `original`; the export time for `edited`
 *  (null when that axis is unknown). */
export function resolveAutoFrameTime(args: {
  source: QuoteFrameSource;
  slide: { start: number; end: number };
  intervals: Interval[];
  transitions?: unknown;
  coverImage?: unknown;
}): number | null {
  const {
    source, slide, intervals, transitions, coverImage,
  } = args;
  if (source === 'original') return frameCaptureSourceTime(slide);
  return resolveEditedFrameTime({
    sourceTime: frameCaptureSourceTime(slide), intervals, transitions, coverImage,
  });
}

export interface FrameCapturePlan {
  videoUrl: string;
  /** Extraction time on `videoUrl`'s own timeline (seconds). */
  time: number;
  /** Horizontal crop center (0..1); null = centered crop. */
  centerX: number | null;
}

/**
 * What to extract for one frame-background slide:
 * - original (automatic or picked): the source video at the SOURCE time
 *   (`min(start + 0.3, end)` or the picked time), cropped around the reframe
 *   center looked up at that same time (`clip.data.cropCenterKeyframes` are
 *   source-axis). No interval mapping — the source video IS that axis;
 * - edited + automatic: the export-timeline time, centered crop (the render
 *   is already framed);
 * - edited + picked: used as-is on the render, centered.
 * Null = capture impossible (source unavailable, unknown export axis, not a
 * frame).
 */
export function resolveFrameCapturePlan(args: {
  sources: FrameCaptureSources;
  intervals: Interval[];
  transitions: unknown;
  coverImage: unknown;
  cropCenterKeyframes: unknown;
  slide: { start: number; end: number; background: QuoteSlideBackground | null };
}): FrameCapturePlan | null {
  const {
    sources, intervals, transitions, coverImage, cropCenterKeyframes, slide,
  } = args;
  const bg = slide.background;
  if (bg?.kind !== 'frame') return null;
  const videoUrl = sources[bg.source];
  if (!videoUrl) return null;

  if (bg.source === 'original') {
    const time = bg.time ?? frameCaptureSourceTime(slide);
    const centerX = resolveCropCenterXAtTime(sanitizeCropCenterKeyframes(cropCenterKeyframes), time);
    return { videoUrl, time, centerX };
  }

  if (bg.time !== null) return { videoUrl, time: bg.time, centerX: null };

  const time = resolveEditedFrameTime({
    sourceTime: frameCaptureSourceTime(slide), intervals, transitions, coverImage,
  });
  return time === null ? null : { videoUrl, time, centerX: null };
}
